import React, { useRef } from 'react';
import {
  Checkbox,
  DateRange,
  Fieldset,
  Form,
  Input,
  NumberParam,
  RequiredHelpPhrase,
  Row,
  SubmitButton,
  Switch,
  TimeRange,
  useCheckboxGroup,
  useForm,
} from '../../../_lib/Form2';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ListBoxModifier, UserSingleSelectComboBoxForm2 } from '../../../components/molecules/Form/components/ComboBox';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { useDelayed } from '../../../hooks/useDelayed';
import { TicketBookingForm } from './TicketBookingEditPartial';
import { useRefreshView } from '../../../_lib/ViewFetcher';

type TicketAddModal = {
  closeModal: () => void;
  form: TicketBookingForm;
};

export function TicketAddModal({ closeModal, form }: TicketAddModal) {
  const { t } = useSonTranslation('TicketBooking');
  const { delayedRefreshView } = useRefreshView();
  const { setElementRef, onCheckboxChange } = useCheckboxGroup(t('edit.form.weekDays.errorMessage'));
  const durationMinutesRef = useRef<HTMLInputElement>(null);
  const timeAsDurationRef = useRef<number>(null);
  const timeRef = useRef<number[]>([-1, -1]);

  const delayedCloseModal = useDelayed(closeModal);

  const { formProps, fetchState, params } = useForm({
    form,
    onSuccess: () => {
      delayedRefreshView();
      delayedCloseModal();
    },
  });

  const dateRange = params.getByName('daterange');
  const timeRange = params.getByName('timerange');
  const weekDays = params.getAllByType('checkbox');
  const durationMinutes = params.getByName('durationMinutes') as NumberParam<any>;
  const parallelTickets = params.getByName('parallelTickets') as NumberParam<any>;
  const supplierUserId = params.getByName('supplierUserId');
  const firstToBeNotified = params.getByName('firstToBeNotifiedUserId');
  const secondToBeNotified = params.getByName('secondToBeNotifiedUserId');

  function setDurationMinutesValue() {
    if (durationMinutesRef.current && timeAsDurationRef.current) {
      durationMinutesRef.current.value = durationMinutesRef.current?.readOnly
        ? timeAsDurationRef.current?.toString()
        : '';
    }
  }

  return (
    <ModalDialog closeModal={closeModal} headline={t(`edit.modalTitle`)} initialFocusIndex={1}>
      <Form id="create-ticket-form" {...formProps}>
        <RequiredHelpPhrase />
        <Fieldset legend={t('edit.form.legendTimeFrameDependent')}>
          <DateRange
            dateRangeParam={dateRange}
            labelStartDate={t(`edit.form.labelStartDate`)}
            labelEndDate={t(`edit.form.labelEndDate`)}
          />
          <Fieldset modifier="like-label" required legend={t('edit.form.weekDaysLabel')}>
            <Row>
              {weekDays.map((param) => {
                return (
                  <Checkbox
                    key={param.name}
                    param={param}
                    ref={setElementRef}
                    onChange={onCheckboxChange}
                    label={
                      // the combination of aria-label and aria-hidden inside is the only way to make sure,
                      // this is announced correctly (full day, not abbreviated) in screen readers.
                      <span
                        aria-label={t(`edit.form.weekDays.${param.name}.title`)}
                        title={t(`edit.form.weekDays.${param.name}.title`)}
                      >
                        <span aria-hidden="true">{t(`edit.form.weekDays.${param.name}.abbr`)}</span>
                      </span>
                    }
                  />
                );
              })}
            </Row>
          </Fieldset>
          <TimeRange
            timeRangeParam={timeRange}
            labelStartTime={t(`edit.form.labelStartTime`)}
            labelEndTime={t(`edit.form.labelEndTime`)}
            onBlur={(event: React.FocusEvent<HTMLInputElement, Element>) => {
              if (event.target?.value) {
                if (event.target?.name === 'startTime') {
                  timeRef.current[0] = event.target?.valueAsNumber;
                } else if (event.target?.name === 'endTime') {
                  timeRef.current[1] = event.target?.valueAsNumber;
                }
              }

              // initialized timeRef.current array with -1 as a null/undefined check as it was before would also
              // validate to false if the value is number 0 as it can be for 0:00 in the morning.
              if (timeRef.current[0] > -1 && timeRef.current[1] > -1) {
                timeAsDurationRef.current = Math.floor((timeRef.current[1] - timeRef.current[0]) / (1000 * 60));

                setDurationMinutesValue();
              }
            }}
          />
        </Fieldset>
        <Fieldset legend={t('edit.form.legendRuleDependent')}>
          <Row>
            <Switch
              label={t(`edit.form.labelSwitchTimeAsDuration`)}
              helpText={t(`edit.form.labelSwitchTimeAsDurationHelpText`)}
              onClick={() => {
                if (durationMinutesRef.current) {
                  durationMinutesRef.current.readOnly = !durationMinutesRef.current?.readOnly;
                }

                setDurationMinutesValue();
              }}
            />
          </Row>
          <Row>
            <Input
              ref={durationMinutesRef}
              param={durationMinutes}
              label={t(`edit.form.labelDurationMinutes`)}
              inputMode="numeric"
              helpText={durationMinutes.constraints?.max
                ? t('edit.form.helpTextDurationMinutes', { max: durationMinutes.constraints.max })
                : undefined}
            />
          </Row>
          <Row>
            <Input
              param={parallelTickets}
              label={t(`edit.form.labelParallelTickets`)}
              helpText={t('edit.form.helpTextParallelTickets')}
              inputMode="numeric"
            />
          </Row>
        </Fieldset>
        <Fieldset legend={t('edit.form.legendSupplierDependent')}>
          <Row>
            <UserSingleSelectComboBoxForm2
              hiddenInputName={supplierUserId.name}
              labelTitle={t(`edit.form.labelSupplierUserId`)}
              helpText={t('edit.form.helpTextSupplierUserId')}
              suggestUrl={supplierUserId.comboBoxUrl.url}
              required={supplierUserId.required}
              modifierListBox={ListBoxModifier.MAX_HEIGHT_200}
            />
          </Row>
          <Row>
            <UserSingleSelectComboBoxForm2
              hiddenInputName={firstToBeNotified.name}
              labelTitle={t(`edit.form.labelFirstPersonToBeNotified`)}
              helpText={t('edit.form.helpTextPersonToBeNotified')}
              suggestUrl={firstToBeNotified.comboBoxUrl.url}
              required={firstToBeNotified.required}
              modifierListBox={ListBoxModifier.MAX_HEIGHT_200}
            />
          </Row>
          <Row>
            <UserSingleSelectComboBoxForm2
              hiddenInputName={secondToBeNotified.name}
              labelTitle={t(`edit.form.labelSecondPersonToBeNotified`)}
              helpText={t('edit.form.helpTextPersonToBeNotified')}
              suggestUrl={secondToBeNotified.comboBoxUrl.url}
              required={secondToBeNotified.required}
              modifierListBox={ListBoxModifier.MAX_HEIGHT_200}
            />
          </Row>
        </Fieldset>
        <SubmitButton
          buttonLabel={t('edit.form.saveButton')}
          messageFail={t('edit.form.status.fail')}
          messageSuccess={t('edit.form.status.success')}
          messageSending={t('edit.form.status.sending')}
          fetchState={fetchState}
          active={fetchState.type === 'initial'}
          customErrorTypeMessages={{
            maxCountExceeded: t('edit.form.status.maxCountExceeded'),
            invalidZeroCount: t('edit.form.status.invalidZeroCount'),
            ticketDurationInvalid: t('edit.form.status.ticketDurationInvalid'),
          }}
        />
      </Form>
    </ModalDialog>
  );
}
